import { useState } from "react";
import { studioAPI, tryParseJSON, API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { CALENDAR30 } from "@/constants/testIds";
import { toast } from "sonner";
import { Calendar, ArrowRight, Package } from "lucide-react";
import AskDirector from "@/components/AskDirector";

export default function Calendar30() {
    const { lang } = useApp();
    const [channel, setChannel] = useState("");
    const [character, setCharacter] = useState("");
    const [theme, setTheme] = useState("");
    const [busy, setBusy] = useState(false);
    const [zipBusy, setZipBusy] = useState(false);
    const [raw, setRaw] = useState("");
    const [calendarId, setCalendarId] = useState(null);
    const parsed = tryParseJSON(raw);
    const episodes = parsed?.episodes || [];

    const run = async () => {
        if (!channel || !theme) { toast.error(lang === "fr" ? "Chaîne + thème requis" : "Channel + theme required"); return; }
        setBusy(true); setRaw(""); setCalendarId(null);
        try {
            const res = await studioAPI.calendar({ channel_name: channel, character, theme, language: lang });
            setRaw(res.result);
            setCalendarId(res.id);
        } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
        finally { setBusy(false); }
    };

    const downloadZip = async () => {
        if (!calendarId) return;
        setZipBusy(true);
        const token = localStorage.getItem("sf_token");
        try {
            toast.info(lang === "fr" ? "Préparation du bundle 30j…" : "Preparing 30-day bundle…");
            const r = await fetch(`${API}/export/calendar/${calendarId}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!r.ok) throw new Error("failed");
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `shorts-factory-30days-${calendarId.slice(0, 8)}.zip`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success(lang === "fr" ? "Bundle 30 jours téléchargé" : "30-day bundle downloaded");
        } catch { toast.error("Download error"); }
        finally { setZipBusy(false); }
    };

    return (
        <div data-testid={CALENDAR30.container} className="space-y-6">
            <div className="grid lg:grid-cols-4 gap-3 items-end">
                <div>
                    <div className="overline">04 · Calendar</div>
                    <h2 className="font-display text-2xl font-black uppercase mt-1">{lang === "fr" ? "30 jours de Shorts" : "30 days of Shorts"}</h2>
                    <div className="mt-2">
                        <AskDirector
                            testid="calendar-ask-director"
                            prompt={lang === "fr" ? "Tu ne sais pas quoi publier demain ? Propose-moi 3 sujets à écrire pour cette semaine." : lang === "es" ? "¿No sabes qué publicar mañana? Propón 3 temas para esta semana." : "Don't know what to post tomorrow? Suggest 3 topics for this week."}
                            label={lang === "fr" ? "Tu ne sais pas quoi publier ?" : lang === "es" ? "¿No sabes qué publicar?" : "Not sure what to post?"}
                        />
                    </div>
                </div>
                <input data-testid={CALENDAR30.channel} value={channel} onChange={(e) => setChannel(e.target.value)}
                    placeholder={lang === "fr" ? "Chaîne (ex: Gaston)" : "Channel (e.g. Gaston)"}
                    className="bg-[#0F141C] border border-[#253046] rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00]" />
                <input data-testid={CALENDAR30.character} value={character} onChange={(e) => setCharacter(e.target.value)}
                    placeholder={lang === "fr" ? "Personnage" : "Character"}
                    className="bg-[#0F141C] border border-[#253046] rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00]" />
                <input data-testid={CALENDAR30.theme} value={theme} onChange={(e) => setTheme(e.target.value)}
                    placeholder={lang === "fr" ? "Thème (vie de bureau…)" : "Theme (office life…)"}
                    className="bg-[#0F141C] border border-[#253046] rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00]" />
            </div>
            <div className="flex flex-wrap items-center gap-3">
                <button data-testid={CALENDAR30.generateBtn} onClick={run} disabled={busy}
                    className="flame-gradient text-black font-semibold px-6 py-3 rounded flex items-center gap-2 kinetic flame-glow disabled:opacity-60">
                    {busy ? "···" : <>{lang === "fr" ? "Générer 30 jours" : "Generate 30 days"} <ArrowRight className="w-4 h-4" /></>}
                </button>
                {calendarId && (
                    <button
                        data-testid="calendar-batch-export-btn"
                        onClick={downloadZip}
                        disabled={zipBusy}
                        className="px-5 py-3 rounded font-semibold border-2 border-[#FF4D00] text-[#FF4D00] hover:bg-[#FF4D00] hover:text-black flex items-center gap-2 kinetic disabled:opacity-60"
                    >
                        <Package className="w-4 h-4" />
                        {zipBusy ? (lang === "fr" ? "Préparation…" : "Preparing…") : (lang === "fr" ? "Télécharger le bundle 30 jours (.zip)" : "Download 30-day bundle (.zip)")}
                    </button>
                )}
            </div>

            <div data-testid={CALENDAR30.result} className="rounded border border-[#253046] bg-[#0B111B] p-5 min-h-[400px]">
                {!raw && !busy && (
                    <div className="h-full flex flex-col items-center justify-center text-center py-20">
                        <Calendar className="w-10 h-10 text-slate-700 mb-3" />
                        <p className="text-sm text-slate-500">{lang === "fr" ? "Un calendrier de 30 épisodes connectés apparaîtra ici." : "A calendar of 30 connected episodes will appear here."}</p>
                    </div>
                )}
                {busy && <div className="py-20 text-center text-slate-400 font-mono-sf text-xs">PLANNING 30 EPISODES ···</div>}
                {episodes.length > 0 && (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2">
                        {episodes.map((e) => (
                            <div key={e.day} className="bg-[#161D2A] border border-[#253046] hover:border-[#FF4D00]/50 kinetic rounded p-3">
                                <div className="flex items-center justify-between">
                                    <div className="font-mono-sf text-[10px] text-[#FF4D00]">JOUR {String(e.day).padStart(2, "0")}</div>
                                    <div className="w-1.5 h-1.5 rounded-full bg-slate-600" />
                                </div>
                                <div className="font-display text-base font-bold uppercase mt-2 leading-tight">{e.title}</div>
                                {e.hook && <div className="text-[11px] text-[#FFB800] mt-2 italic">« {e.hook} »</div>}
                                <p className="text-xs text-slate-400 mt-2 leading-snug">{e.one_line_synopsis}</p>
                                {e.connection_to_previous && (
                                    <div className="mt-2 pt-2 border-t border-[#253046]">
                                        <div className="overline">Lien</div>
                                        <div className="text-[10px] text-slate-500 mt-1">{e.connection_to_previous}</div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                {raw && !parsed && <pre className="text-xs text-slate-300 whitespace-pre-wrap">{raw}</pre>}
            </div>
        </div>
    );
}
